import { useEffect, useMemo, useState } from "react";
import { useTonConnectUI, useTonAddress } from "@tonconnect/ui-react";
import { Address, toNano } from "@ton/core";
import {
  generateNote as coreGenerateNote,
  buildDepositInputs,
  buildDepositBody,
  buildJettonDepositMessage,
  loadCommitmentsFromRelayer,
  RECOMMENDED_JETTON_FORWARD_TON,
} from "@tonado/core";
import { PoolPicker } from "./PoolPicker";
import { NoteCard } from "./NoteCard";
import { Steps } from "./Steps";
import { getPool, listPools, getJettonMeta, assetUnitLabel, type Pool } from "../lib/pools";
import { fmtAmount, fmtTon } from "../lib/format";
import { addStoredNote } from "../lib/storage";
import { NETWORK, RELAYER_URL } from "../lib/env";
import { parseNoteMetadata, type Note } from "../lib/note";
import { getJettonWalletViaRelayer } from "../lib/relayerRpc";

type Stage = "configure" | "note" | "proving" | "send" | "done";

type ProvingPhase = "fetching-tree" | "generating-proof" | "submitting";

// User attaches this much TON for gas on top of the denomination. The pool
// refunds attached − REFUND_RESERVE (~0.01 TON) at the end of the deposit
// handler, so the real cost is ~0.01 TON net regardless of how much is
// attached. We keep it modest so wallet UIs don't show alarming amounts.
const GAS_BUDGET_NANO = 200_000_000n; // 0.2 TON allowance — refunded if unused

export function Deposit() {
  const [tonConnectUI] = useTonConnectUI();
  const sender = useTonAddress();
  const connected = Boolean(sender);

  const defaultKey = listPools().find((p) => p.address)?.key ?? "ton-1";
  const [poolKey, setPoolKey] = useState<string>(defaultKey);
  const [stage, setStage] = useState<Stage>("configure");
  const [provingPhase, setProvingPhase] = useState<ProvingPhase | null>(null);
  const [note, setNote] = useState<Note | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);

  const pool = useMemo(() => getPool(poolKey), [poolKey]);
  const jettonMeta = useMemo(() => (pool ? getJettonMeta(pool) : null), [pool]);
  const unitLabel = useMemo(() => (pool ? assetUnitLabel(pool) : ""), [pool]);
  // Native pools attach denomination + gas as the TON value of the message.
  // Jetton pools attach only the forward-TON (jettons flow through the
  // depositor's own wallet via TEP-74, not as the message's `value` field).
  const totalNano = useMemo(() => {
    if (!pool) return 0n;
    if (jettonMeta) return RECOMMENDED_JETTON_FORWARD_TON + toNano("0.1");
    return BigInt(pool.denomination) + GAS_BUDGET_NANO;
  }, [pool, jettonMeta]);

  useEffect(() => {
    setNote(null);
    setStage("configure");
    setAcknowledged(false);
    setError(null);
    setTxHash(null);
    setProvingPhase(null);
  }, [poolKey]);

  async function handleGenerate() {
    if (!pool) return;
    setError(null);
    try {
      // Generate a real Poseidon-bound deposit note via @tonado/core.
      // This runs the wasm-backed Poseidon to compute commitment +
      // nullifierHash from random preimage. ~30-60ms in modern browsers.
      const { note: raw } = await coreGenerateNote({
        assetId: pool.asset,
        amount: pool.amount,
        network: NETWORK,
      });
      const metadata = parseNoteMetadata(raw);
      if (!metadata) throw new Error("Note generation produced unparseable output");
      setNote(metadata);
      setStage("note");
      // Save to localStorage immediately — if the user navigates away
      // before depositing, the note is preserved.
      addStoredNote(raw);
    } catch (err) {
      console.error("[Deposit.handleGenerate] failed:", err);
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function handleDeposit() {
    if (!pool || !note) return;
    setError(null);
    setStage("proving");
    setProvingPhase("fetching-tree");
    try {
      // (1) Re-parse the note via core's async parser to get the actual
      // Deposit struct (commitment, nullifier, secret) that the merge
      // proof needs as a witness.
      const { parseNote } = await import("@tonado/core");
      const parsed = await parseNote(note.raw);
      const commitment = parsed.deposit.commitment;

      // (2) Pull existing commitments via the relayer's
      // /tree/commitments endpoint, rebuild the off-chain merkle tree,
      // compute the merge proof. All chain reads route through the
      // relayer's server-side toncenter so the dApp ships zero API key.
      // This is the long step (5-15s on commodity hardware).
      const relayerBase = RELAYER_URL.replace(/\/+$/, "");
      const loader = (poolAddress: string) => loadCommitmentsFromRelayer(relayerBase, poolAddress);
      setProvingPhase("generating-proof");
      const built = await buildDepositInputs(loader, pool.address, commitment);

      // (3) Build the on-chain message. The native path sends an op
      // 0xded05701 body directly to the pool. The jetton path sends a
      // TEP-74 transfer to the depositor's OWN jetton wallet (resolved
      // via the relayer's /jetton/wallet-address rather than a direct
      // toncenter call); the forward_payload carries the same proof
      // tuple. The pool's transfer_notification handler verifies the
      // sender is its own jetton wallet (counterfeit-jetton defense).
      let messageTo: string;
      let bodyBase64: string;
      if (jettonMeta) {
        if (!sender) throw new Error("Wallet not connected");
        const depositorJettonWalletStr = await getJettonWalletViaRelayer(jettonMeta.master, sender);
        const built2 = await buildJettonDepositMessage({
          depositorJettonWallet: Address.parse(depositorJettonWalletStr),
          jettonMaster: Address.parse(jettonMeta.master),
          depositor: Address.parse(sender),
          poolAddress: Address.parse(pool.address),
          jettonAmount: BigInt(pool.denomination),
          commitment,
          prevRoot: built.prevRoot,
          newRoot: built.newRoot,
          leafIndex: built.leafIndex,
          mergeProof: built.proof,
        });
        messageTo = built2.destination.toString();
        bodyBase64 = built2.body.toBoc().toString("base64");
      } else {
        const body = await buildDepositBody({
          queryId: 0n,
          commitment,
          prevRoot: built.prevRoot,
          newRoot: built.newRoot,
          leafIndex: built.leafIndex,
          mergeProof: built.proof,
        });
        messageTo = pool.address;
        bodyBase64 = body.toBoc().toString("base64");
      }

      // (4) Send via TON Connect. The user's wallet (Tonkeeper, Tonhub,
      // etc.) handles signing + broadcasting.
      setProvingPhase("submitting");
      setStage("send");
      const res = await tonConnectUI.sendTransaction({
        validUntil: Math.floor(Date.now() / 1000) + 360,
        messages: [
          {
            address: messageTo,
            amount: totalNano.toString(),
            payload: bodyBase64,
          },
        ],
      });
      setTxHash(res.boc);
      setStage("done");
      setProvingPhase(null);
    } catch (err) {
      console.error("[Deposit.handleDeposit] failed:", err);
      setError(err instanceof Error ? err.message : String(err));
      // Roll back to the note step so the user can retry without losing
      // the note (it's already saved to localStorage).
      setStage("note");
      setProvingPhase(null);
    }
  }

  return (
    <div className="flow flow--deposit">
      <Steps
        current={stageIdx(stage)}
        steps={["Choose pool", "Generate note", "Save note", "Send deposit"]}
      />

      <PoolPicker value={poolKey} onChange={setPoolKey} />

      {pool && (
        <div className="summary" aria-live="polite">
          <SummaryRow label="Denomination" value={`${fmtAmount(pool.amount)} ${unitLabel}`} />
          {jettonMeta ? (
            <SummaryRow
              label="Forward TON"
              value={`${fmtTon(totalNano, 3)} TON · covers pool gas`}
              accent
            />
          ) : (
            <>
              <SummaryRow
                label="Gas allowance"
                value={`${fmtTon(GAS_BUDGET_NANO, 2)} TON · refunded`}
              />
              <SummaryRow label="Total to send" value={`${fmtTon(totalNano, 3)} TON`} accent />
            </>
          )}
          <SummaryRow label="Pool address" value={pool.address || "-"} mono />
        </div>
      )}

      {stage === "configure" && (
        <div className="cta-row">
          <button
            type="button"
            className="btn btn--primary btn--lg"
            disabled={!pool?.address}
            onClick={handleGenerate}
          >
            {pool?.address ? "Generate deposit note" : "Pool not yet live"}
            <ArrowRight />
          </button>
        </div>
      )}

      {(stage === "note" || stage === "proving" || stage === "send" || stage === "done") &&
        note && (
          <>
            <NoteCard note={note} />
            {stage === "note" && (
              <>
                <label className="ack">
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                  />
                  <span>
                    I've saved my note somewhere safe. I understand losing it means losing the
                    deposit, and that anyone holding it can withdraw the funds.
                  </span>
                </label>
                <div className="cta-row">
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => setStage("configure")}
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary btn--lg"
                    disabled={!connected || !acknowledged}
                    onClick={handleDeposit}
                  >
                    {!connected ? "Connect wallet to deposit" : "Deposit"}
                    <ArrowRight />
                  </button>
                </div>
                {connected && pool && (
                  <p className="cta-hint">
                    {jettonMeta
                      ? `Your wallet will send ${fmtAmount(pool.amount)} ${unitLabel} plus ~${fmtTon(totalNano, 3)} TON to cover gas. The pool refunds any unused TON to your wallet after the deposit.`
                      : `Your wallet will send ${fmtTon(totalNano, 3)} TON — ${fmtAmount(pool.amount)} TON deposit plus a ${fmtTon(GAS_BUDGET_NANO, 2)} TON gas buffer. The pool refunds any unused gas to your wallet after the deposit, so the net cost is only the small amount of gas actually consumed (~0.01 TON).`}
                  </p>
                )}
              </>
            )}
            {stage === "proving" && <ProvingProgress phase={provingPhase} />}
            {stage === "send" && <Pending label="Confirm in your wallet…" />}
            {stage === "done" && pool && <Success txHash={txHash} pool={pool} />}
          </>
        )}

      {error && (
        <p className="error">
          <strong>Error:</strong> {error}
        </p>
      )}
    </div>
  );
}

function stageIdx(s: Stage): number {
  switch (s) {
    case "configure":
      return 0;
    case "note":
      return 1;
    case "proving":
      return 2;
    case "send":
      return 2;
    case "done":
      return 3;
  }
}

function SummaryRow({
  label,
  value,
  accent,
  mono,
}: {
  label: string;
  value: string;
  accent?: boolean;
  mono?: boolean;
}) {
  return (
    <div className={`summary__row ${accent ? "summary__row--accent" : ""}`}>
      <dt>{label}</dt>
      <dd className={mono ? "mono mono--break" : ""}>{value}</dd>
    </div>
  );
}

function Pending({ label }: { label: string }) {
  return (
    <div className="pending">
      <span className="pending__dot" />
      <span>{label}</span>
    </div>
  );
}

function ProvingProgress({ phase }: { phase: ProvingPhase | null }) {
  const label =
    phase === "fetching-tree"
      ? "Reading pool history…"
      : phase === "generating-proof"
        ? "Generating zero-knowledge proof (5–15s)…"
        : phase === "submitting"
          ? "Submitting to wallet…"
          : "Working…";
  return (
    <div className="pending pending--multi">
      <span className="pending__dot" />
      <div>
        <span>{label}</span>
        <span className="pending__sub">
          All proof generation happens locally in your browser. The relayer never sees your note.
        </span>
      </div>
    </div>
  );
}

function Success({ txHash, pool }: { txHash: string | null; pool: Pool }) {
  const unit = assetUnitLabel(pool);
  return (
    <div className="success">
      <p className="success__kicker">
        <Check /> Deposit submitted
      </p>
      <h3>Wait. Withdraw later. Privacy grows with every block.</h3>
      <p>
        Your commitment will be appended to pool{" "}
        <code>
          {fmtAmount(pool.amount)} {unit}
        </code>{" "}
        within a few seconds. The longer you wait before withdrawing, the larger your anonymity set.
        Your note is saved in this browser under the <em>Notes</em> tab.
      </p>
      {txHash && <p className="success__hash mono mono--break">tx: {txHash.slice(0, 80)}…</p>}
    </div>
  );
}

function ArrowRight() {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden>
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="square"
        d="M3 8h10M9 4l4 4-4 4"
      />
    </svg>
  );
}
function Check() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 8.5l3 3 7-7"
      />
    </svg>
  );
}
